/**
 * Load i18n JSON resources for a locale over HTTP.
 * SSR and browser: absolute CDN URL via env.CDN_BASE_URL.
 * No in-process memoization — rely on CDN/browser HTTP cache.
 */

import { env } from '@/lib/utils/env';
import { i18nConfig, namespaces } from './config';

export type I18nResources = Record<string, Record<string, Record<string, unknown>>>;

export function localeTenantAndStore(): { tenant: string; store: string } {
  const tenant = env.FUNNELS_TENANT_ID?.trim() || 'dev';
  const store = env.FUNNELS_STORE_ID?.trim() || 'default';
  return { tenant, store };
}

/** CDN origin for locale JSON (SSR and browser). */
export function localesBaseUrl(): string {
  return (env.CDN_BASE_URL ?? '').replace(/\/$/, '');
}

export function localeNamespaceUrl(
  locale: string,
  ns: string,
  base: string = localesBaseUrl(),
): string {
  const { tenant, store } = localeTenantAndStore();
  const normalized = base.replace(/\/$/, '');
  return `${normalized}/${tenant}/${store}/locales/${locale}/${ns}.json`;
}

export function localeHttpBackendLoadPath(): string {
  const { tenant, store } = localeTenantAndStore();
  return `${localesBaseUrl()}/${tenant}/${store}/locales/{{lng}}/{{ns}}.json`;
}

async function loadNamespace(locale: string, ns: string): Promise<Record<string, unknown>> {
  try {
    const res = await fetch(localeNamespaceUrl(locale, ns));
    if (!res.ok) return {};
    return (await res.json()) as Record<string, unknown>;
  } catch {
    return {};
  }
}

/**
 * Preload the active locale + default locale (fallback) for every namespace.
 */
export async function loadI18nResources(locale: string): Promise<I18nResources> {
  const localesToLoad = new Set<string>([locale, i18nConfig.defaultLocale]);
  const resources: I18nResources = {};

  await Promise.all(
    [...localesToLoad].map(async (lng) => {
      resources[lng] = {};
      await Promise.all(
        namespaces.map(async (ns) => {
          resources[lng][ns] = await loadNamespace(lng, ns);
        }),
      );
    }),
  );

  return resources;
}
